import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Box,
  Typography,
  Chip,
  Stack,
} from "@mui/material";

export function EditConfigDialog({
  open,  // ✅ Fixed: open instead of isOpen
  config,
  wings,  // ✅ Real wings prop
  parkingTypes,  // ✅ Real parkingTypes prop
  onClose,
  onSave,
}) {
  const [editedConfig, setEditedConfig] = useState(null);

  // Sync when config changes
  useEffect(() => {
    if (config && open) {
      setEditedConfig({
        ...config,
        isIncluded: !(config.isUpgrade || false),  // ✅ Map DB: isUpgrade=true → isIncluded=false
        isUpgradeEligible: config.isUpgradeEligible || false,
        isActive: true,  // Default or from DB
      });
    } else {
      setEditedConfig(null);
    }
  }, [config, open]);

  if (!open || !config || !editedConfig) return null;

  const wing = wings.find((w) => w.id === config.wingId);
  const unitType = wing?.unitTypes?.find((ut) => ut.id === config.unitTypeId);
  const parkingType = parkingTypes?.find((pt) => pt.id === config.parkingTypeId);

  if (!wing || !unitType || !parkingType) return null;

  const handleChipClick = (type) => {
    if (type === 'included') {
      setEditedConfig({
        ...editedConfig,
        isIncluded: true,
        isUpgradeEligible: editedConfig.isUpgradeEligible,  // Keep upgrade eligible
      });
    } else {
      setEditedConfig({
        ...editedConfig,
        isIncluded: false,
        isUpgradeEligible: false,  // Reset when upgrade
      });
    }
  };

  const handleSave = () => {
    // ✅ Map back to DB format
    const saveData = {
      ...config,
      isUpgrade: !(editedConfig.isIncluded || false),  // Included=true → isUpgrade=false
      IsUpgradeEligible: editedConfig.isUpgradeEligible || false,
      isActive: editedConfig.isActive || true,
    };
    onSave(saveData);
    onClose();
  };

  const handleClose = () => {
    setEditedConfig(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Configuration</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Info Section */}
          <Box
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            bgcolor="action.hover"
            p={2}
          >
            <Stack spacing={1}>
              <InfoRow label="Wing" value={wing.name} />
              <InfoRow label="Unit Type" value={unitType.name} />
              <InfoRow label="Parking" value={parkingType.name} />
            </Stack>
          </Box>

          {/* Included / Upgrade */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Parking Type
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label="Included"
                clickable
                color={editedConfig.isIncluded ? "success" : "default"}
                onClick={() => handleChipClick('included')}
              />
              <Chip
                label="Upgrade"
                clickable
                color={!editedConfig.isIncluded ? "warning" : "default"}
                onClick={() => handleChipClick('upgrade')}
              />
            </Stack>
          </Box>

          {/* Upgrade Eligible */}
          {editedConfig.isIncluded && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              border="1px solid"
              borderColor="divider"
              borderRadius={2}
              p={2}
            >
              <Typography variant="body2">
                Upgrade eligible?
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={editedConfig.isUpgradeEligible}
                  onChange={(e) =>
                    setEditedConfig({
                      ...editedConfig,
                      isUpgradeEligible: e.target.checked,
                    })
                  }
                />
                <Typography variant="body2" color="text.secondary">
                  {editedConfig.isUpgradeEligible ? "Yes" : "No"}
                </Typography>
              </Stack>
            </Box>
          )}

       
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="body2" color="text.secondary" minWidth={80}>
        {label}:
      </Typography>
      <Chip label={value} variant="outlined" size="small" />
    </Box>
  );
}
